<script setup lang="ts">
import { ref } from 'vue';
import { CircleStencil, Cropper } from 'vue-advanced-cropper';
import ActionButton from '@/components/buttons/ActionButton.vue';
import { canvasResizer } from '@/helpers/canvas';
import 'vue-advanced-cropper/dist/style.css';

type Props = {
  file: string;
  type: string;
};

defineProps<Props>();
const emit = defineEmits(['save', 'cancel']);

const cropper = ref<typeof Cropper | null>(null);

const zoom = (factor: number) => {
  if (cropper.value) {
    cropper.value.zoom(factor);
  }
};

const rotate = () => {
  if (cropper.value) {
    cropper.value.rotate(90);
  }
};
const flip = () => {
  const x = true;
  const y = false;

  if (cropper.value) {
    if (cropper.value.imageTransforms.rotate % 180 !== 0) {
      cropper.value.flip(!x, !y);
    } else {
      cropper.value.flip(x, y);
    }
  }
};

const defaultSize = ({ imageSize }) => {
  return {
    width: Math.min(imageSize.height, imageSize.width),
    height: Math.min(imageSize.height, imageSize.width),
  };
};
const stencilSize = ({ boundaries }) => {
  return {
    width: Math.min(boundaries.height, boundaries.width) - 48,
    height: Math.min(boundaries.height, boundaries.width) - 48,
  };
};

const cropImage = async () => {
  if (cropper.value) {
    const { canvas } = cropper.value.getResult();

    // we have to send 1080x1080 to server
    const resizedCanvas = canvasResizer(canvas, 1080, 1080);

    const blob = await new Promise((resolve) => resizedCanvas.toBlob(resolve, 'image/jpeg'));
    emit('save', blob);
  }
};
</script>

<template>
  <div class="cropper-container rounded-lg mt-6 mb-2 elevation-6">
    <Cropper
      ref="cropper"
      class="cropper-avatar"
      background-class="cropper-avatar__background"
      :src="file"
      :stencil-size="stencilSize"
      :stencil-props="{
        handlers: {},
        movable: false,
        resizable: false,
        aspectRatio: 1,
        previewClass: 'cropper-avatar__stencil',
      }"
      :default-size="defaultSize"
      image-restriction="stencil"
      :min-width="180"
      :min-height="180"
      :stencil-component="CircleStencil"
    />
  </div>

  <div class="mb-10 flex justify-center items-center">
    <v-btn @click="zoom(0.75)" class="mx-1" variant="plain" icon color="primary">
      <v-icon icon="fas fa-magnifying-glass-minus" />
    </v-btn>
    <v-btn @click="zoom(1.5)" class="mx-1" variant="plain" icon color="primary">
      <v-icon icon="fas fa-magnifying-glass-plus" />
    </v-btn>
    <v-btn @click="rotate" class="mx-1" variant="plain" icon color="primary">
      <v-icon icon="fas fa-rotate-right" />
    </v-btn>
    <v-btn @click="flip" class="mx-1" variant="plain" icon color="primary">
      <v-icon icon="fas fa-shuffle" />
    </v-btn>
  </div>

  <div class="d-flex">
    <ActionButton @click="$emit('cancel')" class="mx-2" cancel>{{
      $t('general.cancel')
    }}</ActionButton>

    <ActionButton @click="cropImage" class="mx-2">{{ $t('general.save') }}</ActionButton>
  </div>
</template>

<style scoped lang="scss">
.cropper {
  &-container {
    display: flex;
    align-items: center;
    width: 220px;
    height: 220px;
    overflow: hidden;
  }
  &-avatar {
    height: 521px;
    &__background {
      background-color: #edf2f4;
    }
    &__foreground {
      background-color: #edf2f4;
    }
    &__stencil {
      border: solid 2px #edf2f4;
    }
  }
}
</style>
